import HtmlHelper from 'foundation/html-helper';
import ComponentsFactory from 'foundation/components-factory';
import modalService from './modal-service';
import template from '../html/default-modal.hbs';
import eventBus from './event-bus';

export const EVENT_MODAL_OPEN = 'modal:open';
export const EVENT_MODAL_OPENED = 'modal:opened';
export const EVENT_MODAL_CLOSE = 'modal:close';
export const EVENT_MODAL_CLOSED = 'modal:closed';
export const SHOW_POPUP_QUERY = '?ShowPopup=api';

export const CLOSE_TIMEOUT = 300;

export const bemClasses = Object.freeze({
    WIDE: 'wide',
    DEKSTOP_WIDE: 'desktop-wide',
    MOBILE_FIXED: 'mobile-fixed',
    CLOSE_OUTSIDE: 'close-outside',
    TITLE: 'has-title',
    AUTO: 'width-auto',
    SPLIT: 'split-form'
});

export default class Modal {
    constructor(content, options = {}) {
        this.content = content;
        this.service = modalService;

        this.element = null;
        this.container = null;
        this.isInited = false;
        this.isDestroyed = false;

        this.defaultOptions = {
            bemList: [],
            destroyOnClose: false,
            resetOnClose: false,
            delegateControl: false,
            onCloseCb: null,
            isLargeContainer: false,
            previousOpenedModal: null
        };

        this.options = { ...this.defaultOptions, ...options };

        this.focusTrap = null;
        this.focusTrapTimeoutId = null;

        this.onKeydown = this.onKeydown.bind(this);
        this.destroyThisElement = this.destroyThisElement.bind(this);
    }

    init() {
        this.element = this.createElement(this.content);
        ComponentsFactory.getInstance().init(this.element);
        this.closeElements = [...this.element.querySelectorAll('[data-modal-close]')];
        this.wrapperElement = this.element.querySelector('[data-modal-wrapper]');
    }

    open(options) {
        if (this.isDestroyed) {
            throw new Error('Cannot open a modal if it\'s already destroyed');
        }

        this.init();
        this.isInited = true;
        this.service.open(this, options);
    }

    close(isPassive = false) {
        this.service.close(this, isPassive);

        if (window.location.href.includes(SHOW_POPUP_QUERY)) {
            window.location.href = window.location.href.split(SHOW_POPUP_QUERY)[0];
        }
    }

    onOpen(options) {
        eventBus.emit(EVENT_MODAL_OPEN, this.container);
        this.addListeners();
        // first animation workaround
        setTimeout(() => {
            // check because might be destroyed
            if (this.element) {
                this.element.classList.add('is-opened');
                this.element.style['-webkit-overflow-scrolling'] = 'touch';
                if (options !== undefined && options.isMastercard === 'is-mastercard') {
                    this.element.classList.add('is-mastercard');
                }

                eventBus.emit(EVENT_MODAL_OPENED, this.container);
            }
        }, 0);
    }

    onClose(isPassive = false) {
        eventBus.emit(EVENT_MODAL_CLOSE, this.container);

        this.element.style['-webkit-overflow-scrolling'] = 'auto';
        this.element.classList.remove('is-opened');
        if (this.element.classList.contains('is-mastercard')) { this.element.classList.remove('is-mastercard'); }
        this.element.classList.add('is-closing');
        this.removeListeners();

        return new Promise((resolve) => {
            setTimeout(() => {
                // check because might be destroyed
                if (this.element) {
                    this.element.classList.remove('is-closing');

                    if (isPassive && this.options.delegateControl) {
                        if (typeof this.options.onCloseCb === 'function') {
                            this.options.onCloseCb();
                        }
                    }

                    if (this.options.destroyOnClose) {
                        this.destroy();
                    }

                    if (this.options.resetOnClose) {
                        this.isInited = false;
                    }
                }
                resolve();
                eventBus.emit(EVENT_MODAL_CLOSED, this.container);
            }, CLOSE_TIMEOUT);
        });
    }

    onCloseClick = (e) => {
        if (e.target !== e.currentTarget) return;
        this.close(true);
    }

    addListeners() {
        if (this.closeElements) {
            this.closeElements.forEach((el) => el.addEventListener('click', this.onCloseClick));
        }

        document.addEventListener('keydown', this.onKeydown);
    }

    removeListeners() {
        if (this.closeElements) {
            this.closeElements.forEach((el) => el.removeEventListener('click', this.onCloseClick));
        }

        document.removeEventListener('keydown', this.onKeydown);
    }

    onKeydown(e) {
        if (e.keyCode === 27) {
            this.close(true);
        }
    }

    /**
     * @param {String|HtmlElement|Function} content
     * @return {HtmlElement}
     */
    createElement(content) {
        content = (typeof content === 'function') ? content() : content;
        let element = (typeof content === 'string') ? HtmlHelper.createElement(content) : content;

        // if we have already created modal element previously we want to find data-modal
        // instead of modal content
        const actualModal = HtmlHelper.getParent(element, '[data-modal]');
        if (actualModal) {
            element = actualModal;
            this.insertModalOnTopOfPrevious(element);
        } else {
            // if current element in not a actual modal node but its content
            const modalElement = HtmlHelper.createElement(template(this.options));
            const modalContentHolder = modalElement.querySelector('[data-modal-content]');
            modalContentHolder.insertAdjacentElement('beforeend', element);
            element = modalElement;
        }

        if (element.parentNode === null || element.ownerDocument !== document) {
            this.getContainer().insertAdjacentElement('beforeend', element);
        }

        return element;
    }

    insertModalOnTopOfPrevious(actualModal) {
        const lastModalFromDomTree = HtmlHelper.getLastNodeOfTypeFromDom('[data-modal]');
        if (lastModalFromDomTree && actualModal && actualModal !== lastModalFromDomTree) {
            lastModalFromDomTree.insertAdjacentElement('afterend', actualModal);
        }
    }

    destroy() {
        this.service.close(this).then(this.destroyThisElement);
    }

    destroyThisElement() {
        if (this.element) {
            ComponentsFactory.getInstance().destroy(this.element);
            this.element.parentNode.removeChild(this.element);
            this.element = null;
            this.isDestroyed = true;
        }
    }

    getContainer() {
        if (this.container === null) {
            this.container = document.body;
        }

        return this.container;
    }

    getWrapperElement() {
        return this.wrapperElement;
    }
}
